import dayjs from 'dayjs';
import type { Reminder, ReminderFilterView } from '@/api/mcp-reminder/interfaces';
import { canUseAdminTenantScope } from '@/utils/authz';

/** Returns true when a reminder is still actionable (not resolved/cancelled). */
export const isReminderOpen = (reminder: Reminder): boolean =>
  reminder.status !== 'RESOLVED' && reminder.status !== 'CANCELLED';

/** Returns true when a reminder is marked as resolved. */
export const isReminderDone = (reminder: Reminder): boolean => reminder.status === 'RESOLVED';

/** Returns true when a reminder is open and either due or past its due time. */
export const isReminderOverdue = (reminder: Reminder): boolean => {
  if (!isReminderOpen(reminder)) return false;
  return reminder.status === 'DUE' || dayjs(reminder.dueAt).isBefore(dayjs());
};

/** Returns true for open reminders that are pending and not yet overdue. */
export const isReminderActive = (reminder: Reminder): boolean =>
  isReminderOpen(reminder) && reminder.status === 'PENDING' && !dayjs(reminder.dueAt).isBefore(dayjs());

/** Returns true for reminders that were already notified but not resolved yet. */
export const isReminderNotified = (reminder: Reminder): boolean =>
  isReminderOpen(reminder) && reminder.status === 'NOTIFIED';

/** Filters reminders according to the current view in reminders UI. */
export const filterRemindersByView = (items: Reminder[], view: ReminderFilterView): Reminder[] => {
  switch (view) {
    case 'active':
      return items.filter(isReminderActive);
    case 'notified':
      return items.filter(isReminderNotified);
    case 'overdue':
      return items.filter(isReminderOverdue);
    case 'done':
      return items.filter(isReminderDone);
    default:
      return items;
  }
};

/** Open reminders for bell badge: active, notified, or overdue. */
export const isBellRelevant = (reminder: Reminder): boolean =>
  isReminderActive(reminder) || isReminderNotified(reminder) || isReminderOverdue(reminder);

/** Sorts bell reminders with overdue first, then all others by nearest due time. */
export const sortRemindersForBell = (items: Reminder[]): Reminder[] => {
  const overdue = items.filter(isReminderOverdue).sort((a, b) => dayjs(a.dueAt).valueOf() - dayjs(b.dueAt).valueOf());
  const rest = items
    .filter((r) => !isReminderOverdue(r) && isBellRelevant(r))
    .sort((a, b) => dayjs(a.dueAt).valueOf() - dayjs(b.dueAt).valueOf());
  return [...overdue, ...rest];
};

/** Returns whether current user can set reminder audience/level options. */
export const canSetReminderLevel = (role?: string, tenantRoles?: string[]): boolean =>
  canUseAdminTenantScope(role) || (tenantRoles?.includes('OWNER') ?? false);
